export const comparisonTools = [
  {
    rank: 1,
    name: 'AutoReception',
    title: 'Best for Fast Setup and High-ROI Local Business Answering',
    useCase: 'Best for contractors, clinics, legal practices, agencies, and local businesses that need a dependable AI phone agent without a long implementation project.',
    features: [
      '24/7 inbound call answering and lead qualification',
      'Calendar booking, missed-call text-back, and call routing',
      'Call recordings, transcripts, summaries, and workflow integrations',
      'Transparent flat-rate SMB pricing with done-for-you setup'
    ],
    pros: ['Fast implementation for local businesses', 'Strong focus on measurable missed-call ROI'],
    cons: ['Less suited to teams building deeply custom voice applications', 'Advanced enterprise requirements may need a custom workflow'],
    pricing: 'From $699/month plus a one-time setup fee. Confirm current plans and included usage before purchase.',
    fit: 'Best overall for SMBs that want a practical phone answering system with minimal technical work.'
  },
  {
    rank: 2,
    name: 'Smith.ai',
    title: 'Best Hybrid Live Agent and AI Dispatch',
    useCase: 'Best for businesses that want AI automation backed by optional live receptionist escalation.',
    features: [
      'AI answering with live-agent handoff options',
      'Lead intake, screening, and appointment scheduling',
      'Call summaries and customer communication workflows',
      'Broad service-business use cases'
    ],
    pros: ['Human backup can help with nuanced calls', 'Established workflow for professional services'],
    cons: ['Pricing can become complex as call volume increases', 'Per-call or per-minute usage may create variable monthly costs'],
    pricing: 'Plans typically use usage-based or package billing. Exact costs depend on call volume, features, and live-agent coverage.'
  },
  {
    rank: 3,
    name: 'My AI Front Desk',
    title: 'Best for Basic Automated Inbound Booking',
    useCase: 'Best for smaller teams that primarily need automated answering, lead capture, and straightforward appointment booking.',
    features: [
      'AI phone answering for common business questions',
      'Appointment scheduling and lead capture',
      'Call summaries and basic routing',
      'Simple onboarding for standard business scenarios'
    ],
    pros: ['Accessible for smaller budgets', 'Good fit for simple inbound workflows'],
    cons: ['May require workarounds for complex routing', 'Usage limits and plan differences should be reviewed carefully'],
    pricing: 'Generally offered through monthly plans with usage allowances. Verify current limits and overage terms before subscribing.'
  },
  {
    rank: 4,
    name: 'Dialpad AI Voice',
    title: 'Best for Unified UCaaS and Internal Team Communications',
    useCase: 'Best for organizations that want phone-system, team messaging, meetings, analytics, and AI in one communications platform.',
    features: [
      'Business phone system and team communications',
      'AI call transcription, summaries, and coaching',
      'Routing, extensions, and administrative controls',
      'Broader UCaaS capabilities beyond receptionist automation'
    ],
    pros: ['Strong internal communications ecosystem', 'Useful analytics for larger support and sales teams'],
    cons: ['Can be more platform than a small business needs', 'Pricing and configuration may require a sales conversation'],
    pricing: 'Usually sold through per-user monthly plans, with final pricing depending on edition, seats, and add-ons.'
  },
  {
    rank: 5,
    name: 'Slang.ai',
    title: 'Best for Restaurants and Hospitality',
    useCase: 'Best for restaurants and hospitality operators handling reservations, hours, menu questions, and high call volumes.',
    features: [
      'Hospitality-focused voice automation',
      'Reservation and location information workflows',
      'Multi-location business support',
      'Call analytics and operational reporting'
    ],
    pros: ['Purpose-built for restaurant call patterns', 'Useful for multi-location hospitality teams'],
    cons: ['Less relevant to contractors and professional services', 'Industry-specific workflows may not translate to other businesses'],
    pricing: 'Pricing is generally quote-based or dependent on locations and call volume. Request a current hospitality-specific quote.'
  },
  {
    rank: 6,
    name: 'Goodcall',
    title: 'Best for Low-Budget Starter Setups',
    useCase: 'Best for solo operators and small teams testing an AI phone agent on a limited call volume.',
    features: [
      'AI answering for common caller questions',
      'Lead capture and basic call routing',
      'Business knowledge configuration',
      'Starter-friendly self-service onboarding'
    ],
    pros: ['Accessible entry point for experimentation', 'Simple use cases can launch quickly'],
    cons: ['Usage-based pricing can rise with call volume', 'Advanced integrations may require additional configuration'],
    pricing: 'Typically uses tiered usage-based plans. Review minutes, phone numbers, transfers, and overage charges carefully.'
  },
  {
    rank: 7,
    name: 'Bland AI',
    title: 'Best for Technical Teams Needing Custom API Builds',
    useCase: 'Best for developers and product teams building custom outbound or inbound voice experiences into their own software.',
    features: [
      'Developer-first voice API and programmable call flows',
      'Custom prompts, webhooks, and application logic',
      'Outbound and inbound calling capabilities',
      'Flexible integration with proprietary systems'
    ],
    pros: ['High flexibility for custom product development', 'Strong option for API-driven workflows'],
    cons: ['Requires technical resources to configure and maintain', 'Per-minute economics may be less predictable at scale'],
    pricing: 'Usually usage-based and billed by minutes or call activity. Confirm model, infrastructure charges, and current voice rates.'
  }
];

export const comparisonRows = [
  ['Starting Price', '$699/month + setup', 'Usage/package based', 'Monthly plans', 'Per-user monthly', 'Quote based', 'Usage based', 'Per-minute usage'],
  ['Free Trial Availability', '3-day trial', 'Plan dependent', 'Plan dependent', 'Plan dependent', 'Demo or quote', 'Plan dependent', 'Test credits or plan dependent'],
  ['Calendar Booking', 'Google, Calendly, CRM workflows', 'Calendar and CRM integrations', 'Common scheduling tools', 'Google and Outlook ecosystem', 'Hospitality reservation tools', 'Common scheduling tools', 'Custom API workflow'],
  ['Missed Call SMS', 'Included workflow', 'Available by plan or workflow', 'Available by plan', 'Available through platform workflows', 'Available by workflow', 'Available by plan', 'Custom implementation'],
  ['Human Transfer / Live Handoff', 'Configurable escalation', 'Strong live-agent option', 'Available by configuration', 'Team routing and transfers', 'Location/team routing', 'Basic routing', 'Custom API logic'],
  ['Setup Time', 'About 5–10 minutes for standard setup', 'Hours to days', 'Minutes to hours', 'Hours to days', 'Days for multi-location setup', 'Minutes to hours', 'Days to weeks for custom builds']
];

export const buyerFaqs = [
  {
    question: 'Which is the best AI receptionist for a service-based small business?',
    answer: 'For most service-based small businesses, the best option is the one that answers immediately, books against real calendar availability, handles missed-call follow-up, and has predictable pricing. AutoReception is a strong fit for contractors, clinics, legal practices, agencies, and other local businesses that want fast setup without building a voice system themselves.'
  },
  {
    question: 'How much does an AI receptionist typically cost per month?',
    answer: 'AI receptionist pricing ranges from low-cost usage plans to several hundred dollars per month. Some providers bill per minute, some bill per user, and others offer flat monthly packages. Compare included minutes, transfers, phone numbers, text messaging, setup fees, and overage rates rather than comparing the headline monthly price alone.'
  },
  {
    question: 'What is the difference between an AI phone agent and a virtual receptionist?',
    answer: 'An AI phone agent is software that answers and processes calls automatically. A virtual receptionist can refer to a human remote receptionist, an AI system, or a hybrid service. The important distinction is whether calls are answered by software, people, or a combination of both.'
  },
  {
    question: 'Can an AI receptionist handle multi-line phone transfers?',
    answer: 'Many AI receptionist platforms can transfer calls to staff, departments, on-call technicians, or external numbers. Confirm whether the provider supports simultaneous calls, department routing, warm transfers, fallback numbers, and after-hours escalation before choosing a plan.'
  },
  {
    question: 'How do AI receptionists prevent hallucinations on pricing and policy questions?',
    answer: 'Reliable systems use a controlled business knowledge base, approved answers, prompt rules, confidence thresholds, and escalation paths. Your team should review the agent regularly, avoid giving it authority to invent pricing, and configure it to collect a callback when information is uncertain.'
  }
];

export const comparisonSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'ItemList',
      name: 'Best AI Receptionist Software for Small Business in 2026',
      url: 'https://autoreception.net/best-ai-receptionist-for-small-business',
      numberOfItems: comparisonTools.length,
      itemListElement: comparisonTools.map((tool) => ({
        '@type': 'ListItem',
        position: tool.rank,
        name: tool.name,
        url: `https://autoreception.net/best-ai-receptionist-for-small-business#${tool.name.toLowerCase().replaceAll(' ', '-')}`
      }))
    },
    {
      '@type': 'FAQPage',
      mainEntity: buyerFaqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer
        }
      }))
    }
  ]
};